import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, History } from 'lucide-react';

import { auditApi, type AuditLogFilters } from '@/features/audit/api';

const PAGE_SIZE = 50;

export function AuditLogPage() {
  const [filters, setFilters] = useState<AuditLogFilters>({
    limit: PAGE_SIZE,
    offset: 0,
  });
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const facets = useQuery({
    queryKey: ['audit-log', 'facets'],
    queryFn: auditApi.facets,
  });

  const page = useQuery({
    queryKey: ['audit-log', filters],
    queryFn: () => auditApi.list(filters),
  });

  const totalPages = useMemo(() => {
    const total = page.data?.total ?? 0;
    return Math.max(1, Math.ceil(total / PAGE_SIZE));
  }, [page.data?.total]);

  const currentPage = Math.floor((filters.offset ?? 0) / PAGE_SIZE) + 1;

  function update<K extends keyof AuditLogFilters>(key: K, value: AuditLogFilters[K]) {
    setFilters((f) => ({ ...f, [key]: value, offset: 0 }));
  }

  async function downloadCsv() {
    const blob = await auditApi.downloadCsv({ ...filters, limit: 5000 });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4">
      <header className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2.5">
            <History className="h-6 w-6 text-primary" />
            Audit log
          </h1>
          <p className="page-subtitle">
            Append-only ledger of every business write. Retention 7 years for financial + identity
            events; configurable elsewhere.
          </p>
        </div>
        <button className="btn-secondary" onClick={downloadCsv}>
          <Download className="h-4 w-4" />
          Export CSV
        </button>
      </header>

      <section className="card grid grid-cols-2 gap-3 md:grid-cols-5">
        <label className="block text-xs">
          Action
          <select
            className="input mt-1 w-full"
            value={filters.action ?? ''}
            onChange={(e) => update('action', e.target.value || undefined)}
          >
            <option value="">All</option>
            {(facets.data?.actions ?? []).map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-xs">
          Entity type
          <select
            className="input mt-1 w-full"
            value={filters.entity_type ?? ''}
            onChange={(e) => update('entity_type', e.target.value || undefined)}
          >
            <option value="">All</option>
            {(facets.data?.entity_types ?? []).map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-xs">
          Actor user id
          <input
            className="input mt-1 w-full font-mono text-xs"
            placeholder="uuid"
            value={filters.actor_user_id ?? ''}
            onChange={(e) => update('actor_user_id', e.target.value.trim() || undefined)}
          />
        </label>
        <label className="block text-xs">
          From
          <input
            className="input mt-1 w-full"
            type="datetime-local"
            value={filters.start ?? ''}
            onChange={(e) => update('start', e.target.value || undefined)}
          />
        </label>
        <label className="block text-xs">
          To
          <input
            className="input mt-1 w-full"
            type="datetime-local"
            value={filters.end ?? ''}
            onChange={(e) => update('end', e.target.value || undefined)}
          />
        </label>
      </section>

      <section className="card !p-0">
        <table className="w-full text-sm">
          <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-4 py-3">When</th>
              <th className="px-4 py-3">Actor</th>
              <th className="px-4 py-3">Action</th>
              <th className="px-4 py-3">Entity</th>
              <th className="px-4 py-3">Request id</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {page.isLoading && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {page.data?.items.length === 0 && !page.isLoading && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                  No audit events match these filters.
                </td>
              </tr>
            )}
            {page.data?.items.map((row) => {
              const isOpen = expandedId === row.id;
              return (
                <Fragment key={row.id}>
                  <tr
                    className="cursor-pointer border-t border-border hover:bg-muted/40"
                    onClick={() => setExpandedId(isOpen ? null : row.id)}
                  >
                    <td className="px-4 py-2 font-mono text-xs">
                      {new Date(row.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">
                      {row.actor_user_id ?? row.actor_type}
                    </td>
                    <td className="px-4 py-2 font-medium">{row.action}</td>
                    <td className="px-4 py-2 text-muted-foreground">
                      {row.entity_type}
                      {row.entity_id && (
                        <span className="ml-1 font-mono text-xs">{row.entity_id.slice(0, 8)}…</span>
                      )}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-muted-foreground">
                      {row.request_id ?? '—'}
                    </td>
                    <td className="px-4 py-2 text-right text-xs text-muted-foreground">
                      {isOpen ? '▾' : '▸'}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="border-t border-border bg-muted/30">
                      <td colSpan={6} className="px-4 py-3">
                        <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-xs">
                          {JSON.stringify(row.payload, null, 2)}
                        </pre>
                        {row.actor_ip && (
                          <p className="mt-2 text-xs text-muted-foreground">
                            Actor IP: <span className="font-mono">{row.actor_ip}</span>
                            {row.actor_user_agent && ` · UA: ${row.actor_user_agent}`}
                          </p>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </section>

      <footer className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {page.data?.total ?? 0} events · page {currentPage} of {totalPages}
        </span>
        <div className="flex gap-2">
          <button
            className="btn-secondary text-xs"
            disabled={(filters.offset ?? 0) === 0}
            onClick={() =>
              setFilters((f) => ({
                ...f,
                offset: Math.max(0, (f.offset ?? 0) - PAGE_SIZE),
              }))
            }
          >
            Previous
          </button>
          <button
            className="btn-secondary text-xs"
            disabled={currentPage >= totalPages}
            onClick={() =>
              setFilters((f) => ({
                ...f,
                offset: (f.offset ?? 0) + PAGE_SIZE,
              }))
            }
          >
            Next
          </button>
        </div>
      </footer>
    </div>
  );
}
